// src/components/molecules/Pagination/index.tsx
import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  className = ''
}) => {
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  return (
    <div className={`flex flex-col items-center gap-3 sm:gap-4 ${className}`}>
      {/* Desktop e Tablet: Layout horizontal completo */}
      <div className="hidden sm:flex flex-col sm:flex-row justify-center items-center gap-4 w-full">
        {/* Botões Voltar/Próximo - Desktop */}
        <div className="flex gap-3 mr-6 lg:mr-10">
          {/* Voltar button */}
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 lg:px-6 py-2 text-sm lg:text-base rounded-full border transition-all duration-200 font-medium ${currentPage > 1
                ? 'text-[#0586c7] border-[#377396] hover:bg-[#377396] hover:text-white hover:shadow-md active:scale-95'
                : 'text-gray-300 border-gray-300 cursor-not-allowed'
              }`}
            aria-label="Página anterior"
          >
            Voltar
          </button>

          {/* Próximo button */}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 lg:px-6 py-2 text-sm lg:text-base rounded-full border transition-all duration-200 font-medium ${currentPage < totalPages
                ? 'text-[#377396] border-[#377396] hover:bg-[#377396] hover:text-white hover:shadow-md active:scale-95'
                : 'text-gray-300 border-gray-300 cursor-not-allowed'
              }`}
            aria-label="Próxima página"
          >
            Próximo
          </button>
        </div>

        {/* Page numbers container - Desktop */}
        <div className="flex items-center gap-1">
          {/* Left arrow */}
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`w-8 h-8 lg:w-9 lg:h-9 border border-r-0 rounded-l-md flex items-center justify-center text-sm lg:text-base transition-all duration-200 ${currentPage > 1
                ? 'border-[#377396] text-[#377396] hover:bg-[#377396] hover:text-white active:scale-95'
                : 'border-gray-300 text-gray-300 cursor-not-allowed'
              }`}
            aria-label="Página anterior"
          >
            &lt;
          </button>

          {/* Page number */}
          <div
            className="w-8 h-8 lg:w-9 lg:h-9 border border-gray-300 flex items-center justify-center text-sm lg:text-base bg-gray-50 font-medium"
            aria-current="page"
            aria-label={`Página ${currentPage} de ${totalPages}`}
          >
            {currentPage}
          </div>

          {/* Right arrow */}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`w-8 h-8 lg:w-9 lg:h-9 border border-l-0 rounded-r-md flex items-center justify-center text-sm lg:text-base transition-all duration-200 ${currentPage < totalPages
                ? 'border-[#377396] text-[#377396] hover:bg-[#377396] hover:text-white active:scale-95'
                : 'border-gray-300 text-gray-300 cursor-not-allowed'
              }`}
            aria-label="Próxima página"
          >
            &gt;
          </button>

          {/* Page counter */}
          <div className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap font-medium">
            de {totalPages}
          </div>
        </div>
      </div>

      {/* Mobile: Layout simplificado */}
      <div className="flex sm:hidden w-full justify-between items-center">
        {/* Botão Voltar - Mobile */}
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`flex items-center gap-2 px-4 py-2 text-sm rounded-full border transition-all duration-200 font-medium ${currentPage > 1
              ? 'text-[#377396] border-[#377396] hover:bg-[#377396] hover:text-white active:scale-95'
              : 'text-gray-300 border-gray-300 cursor-not-allowed'
            }`}
          aria-label="Página anterior"
        >
          <span>&lt;</span>
          <span>Voltar</span>
        </button>

        {/* Contador central - Mobile */}
        <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full">
          <span className="text-sm font-medium text-gray-700">
            {currentPage} de {totalPages}
          </span>
        </div>

        {/* Botão Próximo - Mobile */}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`flex items-center gap-2 px-4 py-2 text-sm rounded-full border transition-all duration-200 font-medium ${currentPage < totalPages
              ? 'text-[#377396] border-[#377396] hover:bg-[#377396] hover:text-white active:scale-95'
              : 'text-gray-300 border-gray-300 cursor-not-allowed'
            }`}
          aria-label="Próxima página"
        >
          <span>Próximo</span>
          <span>&gt;</span>
        </button>
      </div>
    </div>
  );
};
